import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PreconceptionWindowBlog from './blog/preconception-window.tsx';
import SeedCyclingBlog from './blog/seed-cycling-hormonal-balance.tsx';
import ToxinsFertilityBlog from './blog/toxins-fertility.tsx';
import FertilityFoodsBlog from './blog/fertility-foods-hormone-health.tsx';
import WinterSkinHealthBlog from './blog/winter-skin-health.tsx';
import PregnancyPreparationBlog from './blog/pregnancy-preparation-chinese-medicine-nutrition.tsx';
import LinktreePage from './components/LinktreePage';
import CurrimundiClinicPage from './components/CurrimundiClinicPage';
import OnlineConsultationsPage from './components/OnlineConsultationsPage';
import PostpartumPage from './components/PostpartumPage';
import TeensPage from './components/TeensPage';
import YoungAdultsPage from './components/YoungAdultsPage';
import PregnancyPage from './components/PregnancyPage';
import IVFSupportPage from './components/IVFSupportPage';
import PerimenopausePage from './components/PerimenopausePage';
import MenopausePage from './components/MenopausePage';
import PreconceptionPage from './components/PreconceptionPage';
import ScrollDownArrow from "@/components/ScrollDownArrow";
import NavBar from './components/NavBar';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import TeamSection from './components/TeamSection';
import LocationsSection from './components/LocationsSection';
import ServicesSection from './components/ServicesSection';
import LifeStageSection from './components/LifeStageSection';
import PricingSection from './components/PricingSection';
import BlogSection from '@/components/BlogSection';
import FAQSection from '@/components/FAQSection'; 
import BookingSection from './components/BookingSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import { ReturnToTop } from './components/ReturnToTop';
import PerformanceOptimizer from '@/components/PerformanceOptimizer';
import { WelcomeModal } from './components/WelcomeModal';
import BirthPrepPage from './components/BirthPrepPage';
import MiscarriageSupportPage from './components/MiscarriageSupportPage';
import CosmeticAcupuncturePage from './components/CosmeticAcupuncturePage';
import SkinHealthPage from './components/SkinHealthPage';
import StressBurnoutPage from './components/StressBurnoutPage';
import PainInjuryPage from './components/PainInjuryPage';
import SleepSupportPage from './components/SleepSupportPage';
import DigestionGutHealthPage from './components/DigestionGutHealthPage';
import ImmuneSupportPage from './components/ImmuneSupportPage';
import HeadacheMigrainePage from './components/HeadacheMigrainePage';
import EmotionalHealingPage from './components/EmotionalHealingPage';
import FertilitySupportPage from './components/FertilitySupportPage';
import BottomNavBar from './components/BottomNavBar';
import watwLogo from './assets/watw_logo.webp';
import CycleFreeHealthPage from './components/CycleFreeHealthPage';
import TermsConditionsPage from './components/TermsConditionsPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import MedicalDisclaimerPage from './components/MedicalDisclaimerPage';
import MedicalOrganizationSchema from './components/MedicalOrganizationSchema';
import SkinPackagePage from './components/care-packages/SkinPackagePage';
import FertilityPackagePage from './components/care-packages/FertilityPackagePage';
import OfferPopup from './components/OfferPopup';

const MobileBrandHeader = () => (
  <header className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-amber-100 bg-white sticky top-0 z-[61]">
    <img src={watwLogo} alt="Women and the Way logo" className="w-8 h-8" />
    <span className="font-serif text-lg font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">Women and the Way</span>
  </header>
);

const App: React.FC = () => {
  const [showOfferPopup, setShowOfferPopup] = useState(true);

  useEffect(() => {
    // Update canonical URL based on current path
    const baseUrl = 'https://womenandtheway.com.au';
    const path = window.location.pathname;
    const canonicalUrl = path === '/' ? baseUrl : `${baseUrl}${path}`;
    // Remove existing canonical link if any
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    // Create and append new canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
      <MedicalOrganizationSchema />
      <PerformanceOptimizer />
      <div className="desktop-navbar">
        <NavBar />
      </div>
      <MobileBrandHeader />
      <Routes>
        <Route path="/" element={
          <main>
            <Hero />
            <AboutSection />
            <TeamSection />
            <LocationsSection />
            <ServicesSection />
            <LifeStageSection />
            <PricingSection />
            <FAQSection />
            <BookingSection />
            <BlogSection />
            <ContactSection />
          </main>
        } />
        <Route path="/blog/preconception-window" element={<PreconceptionWindowBlog />} />
        <Route path="/blog/seed-cycling-hormonal-balance" element={<SeedCyclingBlog />} />
        <Route path="/blog/toxins-fertility" element={<ToxinsFertilityBlog />} />
        <Route path="/blog/fertility-foods-hormone-health" element={<FertilityFoodsBlog />} />
        <Route path="/blog/winter-skin-health" element={<WinterSkinHealthBlog />} />
        <Route path="/blog/pregnancy-preparation-chinese-medicine-nutrition" element={<PregnancyPreparationBlog />} />
        <Route path="/blog" element={<BlogSection />} />
        <Route path="/links" element={<LinktreePage />} />
        <Route path="/coolum-acupuncture" element={<Navigate to="/currimundi-clinic" replace />} />
        <Route path="/currimundi-clinic" element={<CurrimundiClinicPage />} />
        <Route path="/online-consultations" element={<OnlineConsultationsPage />} />
        <Route path="/postpartum" element={<PostpartumPage />} />
        <Route path="/teens" element={<TeensPage />} />
        <Route path="/young-adults" element={<YoungAdultsPage />} />
        <Route path="/pregnancy" element={<PregnancyPage />} />
        <Route path="/ivf-support" element={<IVFSupportPage />} />
        <Route path="/perimenopause" element={<PerimenopausePage />} />
        <Route path="/menopause" element={<MenopausePage />} />
        <Route path="/preconception" element={<PreconceptionPage />} />
        <Route path="/birth-prep" element={<BirthPrepPage />} />
        <Route path="/miscarriage-support" element={<MiscarriageSupportPage />} />
        <Route path="/cosmetic-acupuncture" element={<CosmeticAcupuncturePage />} />
        <Route path="/skin-health" element={<SkinHealthPage />} />
        <Route path="/stress-burnout" element={<StressBurnoutPage />} />
        <Route path="/pain-injury" element={<PainInjuryPage />} />
        <Route path="/sleep-support" element={<SleepSupportPage />} />
        <Route path="/digestion-gut-health" element={<DigestionGutHealthPage />} />
        <Route path="/immune-support" element={<ImmuneSupportPage />} />
        <Route path="/headache-migraine" element={<HeadacheMigrainePage />} />
        <Route path="/emotional-healing" element={<EmotionalHealingPage />} />
        <Route path="/fertility-support" element={<FertilitySupportPage />} />
        <Route path="/cycle-free-health" element={<CycleFreeHealthPage />} />
        <Route path="/care-packages/skin-package" element={<SkinPackagePage />} />
        <Route path="/care-packages/fertility-package" element={<FertilityPackagePage />} />
        <Route path="/terms-conditions" element={<TermsConditionsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
        {/* Add a catch-all route for 404s if desired */}
      </Routes>
      <Footer />
      <BottomNavBar />
      <ScrollDownArrow />
      <ReturnToTop />
      <WelcomeModal />
      <OfferPopup 
        isOpen={showOfferPopup}
        onClose={() => setShowOfferPopup(false)}
        offerType="intro-skin"
        showOnPages={['/', '/skin-health']}
        delay={5000}
      />
    </div>
  );
};

export default App;
